import React, { useMemo } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import IVButton from '@invideoio/uniformd/components/Button';
import { PromotionDetailProps } from './types';
import { useAppSelector } from '@store/hook';
import { ClickCtaUxSource, ClickCtaType } from '../../types/analytics';
import { deviceStateSelector } from '@store/device-state';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';

const Detail = (props: PromotionDetailProps) => {
  const {
    paragraph,
    highlightedParagraph,
    buttonLabel,
    subInfo,
    MobileAppLink,
    onSignUp,
    link,
  } = props;

  const deviceState = useAppSelector(deviceStateSelector);

  const { platform, os } = deviceState;

  const onCTAClick = () => {
    if (link) {
      window.open(link.toString(), '_self');
    } else {
      onSignUp({
        ux_source: ClickCtaUxSource.Bottom,
        type: ClickCtaType.ClickSignup,
        btn_position: 'Bottom',
      });
    }
  };

  const isIosBadgeVisible = useMemo(() => {
    return (
      MobileAppLink?.IOSAppLink &&
      (platform === PlatformType.Desktop || os === 'iOS')
    );
  }, [os, platform, MobileAppLink]);

  const isAndroidBadgeVisible = useMemo(() => {
    return (
      MobileAppLink?.AndroidAppLink &&
      (platform === PlatformType.Desktop || os === 'Android')
    );
  }, [os, platform, MobileAppLink]);

  return (
    <React.Fragment>
      <div
        className={ivclass(
          'iv-text-left sm:iv-text-center',
          'iv-font-semibold',
        )}
      >
        <span
          className={ivclass(
            'sm:iv-m-body-2xl md:iv-m-heading-4xs lg:iv-m-heading-4xs xl:iv-m-heading-3xs',
            'sm:iv-text-body-2xl md:iv-text-heading-4xs lg:iv-text-heading-4xs xl:iv-text-heading-3xs',
          )}
        >
          {paragraph}
          <span className={ivclass('iv-text-grey-50')}>
            {highlightedParagraph}
          </span>
        </span>
      </div>
      {MobileAppLink ? (
        <div className="iv-text-center iv-flex iv-flex-col iv-items-start md:iv-items-center iv-mt-50 sm:iv-items-center sm:iv-flex-col iv-gap-30 md:iv-mb-40 sm:iv-mb-30">
          <div className="iv-flex iv-gap-[18px]">
            {isIosBadgeVisible && (
              <a href={MobileAppLink.IOSAppLink}>
                <ImageComponent
                  elementype="native"
                  src={MobileAppLink.IOSBadge?.url}
                  alt="QR Code"
                  className="iv-w-[160px] iv-h-[54px]"
                />
              </a>
            )}
            {isAndroidBadgeVisible && (
              <a href={MobileAppLink.AndroidAppLink}>
                <ImageComponent
                  elementype="native"
                  src={MobileAppLink.AndroidBadge?.url}
                  alt="QR Code"
                  className="iv-w-[160px] iv-h-[54px]"
                />
              </a>
            )}
          </div>
          {MobileAppLink.QRCode && <div className="iv-mt-50 md:iv-mt-40 sm:iv-mt-30">
            <ImageComponent
              elementype="native"
              src={MobileAppLink.QRCode.url}
              alt="QR Code"
              className="iv-w-[124px] iv-h-[124px]"
            />
          </div>}
        </div>
      ) : (
        <div className={ivclass('iv-inline-block', 'iv-pt-40 sm:iv-pt-50')}>
          <IVButton
            onClick={onCTAClick}
            className={ivclass(
              'iv-flex',
              'iv-justify-center',
              'iv-items-center',
              'sm:iv-w-[222px] md:iv-w-[222px] lg:iv-w-[268px] xl:iv-w-[268px]',
              'sm:iv-h-[52px] md:iv-h-[52px]  lg:iv-h-[65px] xl:iv-h-[65px]',
              'iv-rounded-full',
              'iv-bg-blue hover:iv-bg-blue-strong iv-text-grey-0',
              'iv-font-bold',
            )}
          >
            <span
              className={ivclass(
                'sm:iv-m-body-2xl md:iv-m-body-xl lg:iv-m-body-3xl xl:iv-m-body-3xl',
                'sm:iv-text-body-2xl md:iv-text-body-xl lg:iv-text-body-3xl xl:iv-text-body-3xl',
              )}
            >
              {buttonLabel}
            </span>
          </IVButton>
          <div
            className={ivclass(
              'iv-py-[16px] sm:iv-pt-10',
              'iv-text-center',
              'iv-text-body-m sm:iv-text-body-xs',
              'iv-font-medium',
              'iv-text-grey-50',
            )}
          >
            {subInfo}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Detail;
